/* Seven days against one clock.
 *
 * The distance between the day and the month, and it answers what neither can.
 * The day is too close to see a pattern: every afternoon looks like an
 * afternoon. The month is too far to see an hour: a full Tuesday and an empty
 * one are the same size of square. "Every afternoon this week is already gone"
 * is only visible at seven days, and it is the single most useful thing a
 * planner can tell somebody on a Monday.
 *
 * So unlike the month, this draws real blocks at real positions — a column per
 * day, laid against the hours down the side. Where a thing sits *is* the
 * information here, which is why the payload carries whole blocks rather than
 * a summary.
 *
 * The hour range is not 00:00–24:00. A week drawn across the full day spends
 * two thirds of its height on sleep, and every real block is squeezed into the
 * middle third — so it spans from an hour before the earliest thing to an hour
 * after the latest, with the working day as the floor. The scale is honest
 * because it is stated at the edge, not because it is fixed.
 */

import { useEffect, useMemo, useState } from 'react';
import { readContext } from '../ui/dayContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Block, WeekSummary } from '@shared/types';
import { get, patch, errorMessage } from '../api';
import { useSession, localToday } from '../session';
import ViewSwitch from '../ui/ViewSwitch';
import Icon from '../ui/Icon';
import { hhmm, hours, shiftDate, human } from '../ui/time';
import { useBlockDrag } from '../ui/dragBlock';
import { useToast } from '../ui/toast';
import { useIsPhone } from '../ui/useIsPhone';


/** The Monday on or before a date — the same week boundary the server uses. */
function weekStart(date: string): string {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

export default function WeekScreen() {
  const { user, today, loading } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const realToday = today ?? localToday(user?.tz);

  const asked = params.get('date');
  const anchor = asked && /^\d{4}-\d{2}-\d{2}$/.test(asked) ? asked : realToday;
  const from = weekStart(anchor);

  const week = useQuery({
    queryKey: ['week', from],
    enabled: !loading,
    queryFn: async () => {
      const r = await get<WeekSummary>(`/api/plan/week?from=${from}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not load that week.'));
      return r;
    }
  });

  const [showAll, setShowAll] = useState(false);
  /* Seven columns do not fit a phone: at 347px every day is 50px wide and
     every title is three letters. On a phone the week is a list of days. */
  const phone = useIsPhone();
  const qc = useQueryClient();
  const toast = useToast();

  /* Dragging is the same PATCH the Move dialog uses, so a block dropped by
     hand obeys the rules a block placed by hand obeys: sleep is protected,
     and a clash comes back naming what is in the way rather than silently
     overwriting it. */
  const moved = useMutation({
    mutationFn: async ({ id, date, startMin, minutes }:
      { id: string; date: string; startMin: number; minutes?: number }) => {
      const r = await patch(`/api/plan/block/${id}`,
        minutes == null ? { date, startMin } : { minutes });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not move it.'));
      return { date, startMin, minutes };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['week'] });
      void qc.invalidateQueries({ queryKey: ['plan'] });
      void qc.invalidateQueries({ queryKey: ['month'] });
    },
    /* Spoken rather than swallowed: the block springs back on its own because
       nothing was written, and the sentence says why. */
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  /* A block carries no date of its own — the day it is on does — so the
     lookup returns both. */
  const blockById = (id: string) => {
    for (const d of days) {
      const b = d.blocks.find(x => x.id === id);
      if (b) return { block: b, date: d.date };
    }
    return null;
  };

  const dragging = useBlockDrag({
    onMove: (id, at) => {
      const found = blockById(id);
      if (!found) return;
      const { block, date } = found;
      if (at.date === date && at.startMin === block.startMin) return;   // nothing moved
      moved.mutate({ id, date: at.date, startMin: at.startMin });
    },
    onResize: (id, minutes) => {
      const found = blockById(id);
      if (!found || minutes === found.block.endMin - found.block.startMin) return;
      moved.mutate({ id, date: found.date, startMin: found.block.startMin, minutes });
    }
  });

  useEffect(() => {
    document.title = 'The week · Amon';
    return () => { document.title = 'Amon'; };
  }, []);

  const days = week.data?.days ?? [];
  const occasions = week.data?.occasions ?? {};

  /* The window worth drawing. An hour of air either side of what is actually
     there, never narrower than the working day, and the whole clock only if
     somebody asks for it. */
  const [top, bottom] = useMemo(() => {
    const all = days.flatMap(d => d.blocks);
    if (showAll || !all.length) return showAll ? [0, 1440] : [user?.workStart ?? 540, user?.workEnd ?? 1080];
    const earliest = Math.min(...all.map(b => b.startMin), user?.workStart ?? 540);
    const latest = Math.max(...all.map(b => b.endMin), user?.workEnd ?? 1080);
    return [Math.max(0, earliest - 60), Math.min(1440, latest + 60)];
  }, [days, showAll, user?.workStart, user?.workEnd]);

  const span = Math.max(60, bottom - top);
  const place = (b: Block) => ({
    top: `${((b.startMin - top) / span) * 100}%`,
    height: `${(Math.max(15, b.endMin - b.startMin) / span) * 100}%`
  });

  const hourMarks = useMemo(() => {
    const step = span > 14 * 60 ? 180 : span > 8 * 60 ? 120 : 60;
    const out: number[] = [];
    for (let m = Math.ceil(top / step) * step; m < bottom; m += step) out.push(m);
    return out;
  }, [top, bottom, span]);

  const totals = days.reduce((a, d) => a + d.plannedMin, 0);
  const go = (date: string) => { setParams({ date }); };

  return (
    <div className="screen">
      <ViewSwitch />

      <header className="topbar">
        <div>
          <h1>The week</h1>
          <p className="muted">
            {week.data
              ? `${new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' })
                  .format(new Date(from + 'T12:00:00Z'))} – ` +
                `${new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' })
                  .format(new Date(week.data.to + 'T12:00:00Z'))} · ${hours(totals)} planned`
              : 'seven days against one clock'}
          </p>
        </div>
        <div className="inline">
          <button className="btn btn-ghost btn-icon" onClick={() => go(shiftDate(from, -7))}
                  aria-label="The week before" title="The week before"><Icon name="left" /></button>
          <button className="btn btn-ghost btn-sm" onClick={() => setParams({})}><Icon name="today" size={16} />This week</button>
          <button className="btn btn-ghost btn-icon" onClick={() => go(shiftDate(from, 7))}
                  aria-label="The week after" title="The week after"><Icon name="right" /></button>
        </div>
      </header>

      {week.isError && (
        <div className="notice notice-bad" role="alert">
          <p><strong>Could not load that week.</strong></p>
          <p className="muted small">{(week.error as Error).message}</p>
          <button className="btn btn-ghost btn-sm" onClick={() => week.refetch()}>Try again</button>
        </div>
      )}

      {phone ? (
        <WeekAgenda days={days} today={realToday} occasions={occasions}
                    onOpen={date => navigate(`/day?date=${date}`)} />
      ) : (
      <div className="weekgrid">
        <div className="weekgrid-hours" aria-hidden="true">
          {hourMarks.map(m => (
            <span key={m} style={{ top: `${((m - top) / span) * 100}%` }}>{hhmm(m)}</span>
          ))}
        </div>

        {days.map(d => {
          const isToday = d.date === realToday;
          const marks = occasions[d.date] ?? [];
          const label = new Intl.DateTimeFormat(undefined,
            { weekday: 'short', day: 'numeric', timeZone: 'UTC' })
            .format(new Date(d.date + 'T12:00:00Z'));

          return (
            <section key={d.date} className={`weekday${isToday ? ' is-today' : ''}`}
                     aria-label={`${label}, ${d.plannedMin ? hours(d.plannedMin) + ' planned' : 'nothing planned'}`}>
              <h2 className="weekday-head">
                <button className="linkish" onClick={() => navigate(`/day?date=${d.date}`)}>
                  {label}
                </button>
                {d.plannedMin > 0 && <span className="muted small">{hours(d.plannedMin)}</span>}
              </h2>

              {/* Birthdays sit above the clock rather than in it: they are facts
                  about the day, not things that happen at a time. */}
              {marks.map(o => (
                <p key={o.id} className="weekday-occasion" title={o.title}>
                  <Icon name={o.kind === 'birthday' ? 'cake' : 'star'} size={11} /> {o.title}
                </p>
              ))}

              {readContext(d.context).label && <p className="weekday-context">{readContext(d.context).label}</p>}

              <div className="weekday-track"
                   data-drop-date={d.date}
                   data-top-min={top}
                   data-span-min={span}>
                {hourMarks.map(m => (
                  <span key={m} className="weekday-rule" aria-hidden="true"
                        style={{ top: `${((m - top) / span) * 100}%` }} />
                ))}
                {d.blocks.map((b, i) => {
                  const held = dragging.drag?.id === b.id;
                  /* An appointment's blocks are rebuilt from the appointment,
                     so a drag here would be undone the next time it changed.
                     They stay put, and say so. */
                  const movable = !!b.id && !b.apptId;

                  return (
                    <div
                      key={b.id ?? `${b.startMin}-${i}`}
                      className={`weekblock block-${b.kind}${b.locked ? ' is-locked' : ''}` +
                        `${held ? ' is-dragging' : ''}${movable ? ' is-movable' : ''}`}
                      style={place(b)}
                      title={movable
                        ? `${b.start}–${b.end} ${b.title} — drag to move`
                        : `${b.start}–${b.end} ${b.title}`}
                      onPointerDown={e => movable && dragging.start(e, b.id!, b.startMin)}
                      onPointerMove={dragging.move}
                      onPointerUp={dragging.end}
                      onPointerCancel={dragging.cancel}>
                      <button type="button" className="weekblock-open"
                              onClick={() => navigate(`/day?date=${d.date}`)}>
                        <em>{b.start}</em>
                        <span>{b.title}</span>
                      </button>
                      {movable && (
                        /* The foot resizes rather than moves — the same gesture
                           every calendar uses for "until when". */
                        <span className="weekblock-grip"
                              aria-hidden="true"
                              onPointerDown={e => {
                                e.stopPropagation();
                                dragging.start(e, b.id!, b.startMin, true);
                              }}
                              onPointerMove={dragging.move}
                              onPointerUp={dragging.end} />
                      )}
                    </div>
                  );
                })}
                {d.blocks.length === 0 && !week.isPending && (
                  <span className="weekday-empty" aria-hidden="true">—</span>
                )}
              </div>
            </section>
          );
        })}
      </div>
      )}

      {dragging.drag?.at && (
        <p className="drag-readout" role="status">
          {dragging.drag.resizing
            ? `Until ${hhmm(dragging.drag.at.startMin)} — ` +
              `${human(Math.max(15, dragging.drag.at.startMin - dragging.drag.fromMin))}`
            : `${new Intl.DateTimeFormat(undefined, { weekday: 'long', timeZone: 'UTC' })
                .format(new Date(dragging.drag.at.date + 'T12:00:00Z'))} at ` +
              hhmm(dragging.drag.at.startMin)}
        </p>
      )}

      <div className="inline wrap week-fit">
        <button className="btn btn-ghost btn-sm" onClick={() => setShowAll(s => !s)}
                aria-pressed={showAll}>
          <Icon name={showAll ? 'target' : 'clock'} size={15} />
          {showAll ? 'Fit to what is planned' : 'Show the whole day'}
        </button>
        <span className="muted small">
          {showAll
            ? 'Midnight to midnight, including the hours you are asleep.'
            : `Showing ${hhmm(top)} to ${hhmm(bottom)} — an hour either side of what is on.`}
        </span>
      </div>
    </div>
  );
}

/* ── the week on a phone ─────────────────────────────────────────
   A card per day: the date, how full it is, anything worth remembering, and
   the blocks in order with their times. Tapping a day opens it, where moving
   things is comfortable — dragging a 20px block across a 50px column is not. */
function WeekAgenda({ days, today, occasions, onOpen }: {
  days: WeekSummary['days'];
  today: string;
  occasions: WeekSummary['occasions'];
  onOpen: (date: string) => void;
}) {
  return (
    <ol className="week-agenda" aria-label="The week, day by day">
      {days.map(d => {
        const isToday = d.date === today;
        const label = new Intl.DateTimeFormat(undefined,
          { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' })
          .format(new Date(d.date + 'T12:00:00Z'));
        const marks = (occasions ?? {})[d.date] ?? [];
        return (
          <li key={d.date} className={`agenda-day${isToday ? ' is-today' : ''}`}>
            <button type="button" className="agenda-head" onClick={() => onOpen(d.date)}
                    aria-label={`Open ${label}${isToday ? ', today' : ''}`}>
              <span className="agenda-date">{label}</span>
              {isToday && <span className="chip chip-now">today</span>}
              <span className="muted small">{d.plannedMin ? `${hours(d.plannedMin)} planned` : 'free'}</span>
              <Icon name="right" size={16} />
            </button>
            {marks.map(o => (
              <p key={o.id} className="agenda-mark">
                <Icon name={o.kind === 'birthday' ? 'cake' : 'star'} size={12} />{o.title}
              </p>
            ))}
            {readContext(d.context).label && <p className="agenda-mark">{readContext(d.context).label}</p>}
            {d.blocks.length > 0 && (
              <ul className="agenda-blocks">
                {d.blocks.map((b, i) => (
                  <li key={b.id ?? `${b.startMin}-${i}`} className={`agenda-block block-${b.kind}`}>
                    <span className="agenda-time">{b.start || hhmm(b.startMin)}–{b.end || hhmm(b.endMin)}</span>
                    <span className="agenda-title">{b.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}
